/**
 * nt-lead-hero.js
 * Drives the [data-nt-lead-hero] form: per-field validation (name, email,
 * phone, logo are required — errors show right under each field), a
 * multi-file logo/artwork picker that uploads each file the instant it's
 * selected or dropped (not deferred to submit time), with per-file
 * progress/retry and individual or bulk removal, indoor/outdoor +
 * style-preference pickers (data from window.ntSignData.STYLE_DEFS), a
 * "normal" submit that posts the lead once every file has finished
 * uploading, and an independent "prospect" submission that saves partial
 * field data as the user fills the form. Requires nt-quote-api.js first.
 */
(function () {
  'use strict';

  var API = window.NTQuoteAPI;

  function escName(s) {
    return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  // OpenAI click id: ?oppref= in the URL, else the pixel's __oppref cookie, else ''.
  function getOppref() {
    try {
      var m = /[?&]oppref=([^&#]*)/.exec(window.location.search);
      if (m && m[1]) return decodeURIComponent(m[1]);
    } catch (e) { }
    try {
      var c = /(?:^|;\s*)__oppref=([^;]*)/.exec(document.cookie);
      if (c && c[1]) return decodeURIComponent(c[1]);
    } catch (e) { }
    return '';
  }

  // nt-quote-api.js replaces its default tracking_metadata wholesale when the
  // payload carries one, so merge oppref into the live metadata instead.
  function trackingMetadataWithOppref() {
    var base = {};
    try {
      if (typeof window.getLeadTrackingData === 'function') base = window.getLeadTrackingData() || {};
    } catch (e) { }
    return Object.assign({}, base, { oppref: getOppref() });
  }

  function init(root) {
    if (root.dataset.ntBound) return;
    root.dataset.ntBound = 'true';

    if (!API) {
      console.error('nt-lead-hero.js: window.NTQuoteAPI is unavailable — the quote form cannot validate or submit.');
      var form0 = root.querySelector('[data-nt-lead-form]');
      if (form0) {
        form0.addEventListener('submit', function (e) {
          e.preventDefault();
          var errorBox0 = root.querySelector('[data-nt-lead-error]');
          if (errorBox0) {
            errorBox0.textContent = 'This form is temporarily unavailable — please call us directly.';
            errorBox0.hidden = false;
          }
        });
      }
      return;
    }

    var form = root.querySelector('[data-nt-lead-form]');
    var successView = root.querySelector('[data-nt-lead-success]');
    var errorBox = root.querySelector('[data-nt-lead-error]');
    var resetBtn = root.querySelector('[data-nt-lead-reset]');
    var firstNameEl = root.querySelector('[data-nt-lead-firstname]');
    var emailEl = root.querySelector('[data-nt-lead-email]');
    var phoneEl = root.querySelector('[data-nt-lead-phone]');
    var recordSource = root.dataset.ntLeadRecordSource || 'Landing Page 1';
    var selectedUsage = {};
    var selectedStyles = {};
    var prospect = API.createProspectTracker();
    var leadTracker = API.createLeadTracker(prospect.uuid);
    var fileManager = createFileManager();

    // Spam gate helpers live in assets/spam-gate.js (window.SpamGate). It owns
    // window.leadFormState, the scroll listener, and the 4-condition check.
    // evaluateSpam() expects real checkbox DOM elements (a signTypeSelector +
    // placementIds), but this form's sign-type/indoor-outdoor pickers are
    // custom cells/buttons, not checkboxes — so their selection state is
    // mirrored onto hidden checkboxes scoped to this instance (uid) instead
    // of touching spam-gate.js itself. uid also keeps the email/name field
    // ids unique per instance, since chatbot-lead-form-v2 (site-wide) and
    // nt-lead-hero-v1 (page section) can both render [data-nt-lead-hero] on
    // the same page at once.
    var uid = 'ntlh' + Math.random().toString(36).slice(2, 9);
    var spamPlacementIds = { Indoor: 'nt-lead-spam-indoor-' + uid, Outdoor: 'nt-lead-spam-outdoor-' + uid };
    var spamIndoorCb = document.createElement('input');
    spamIndoorCb.type = 'checkbox';
    spamIndoorCb.id = spamPlacementIds.Indoor;
    spamIndoorCb.hidden = true;
    var spamOutdoorCb = document.createElement('input');
    spamOutdoorCb.type = 'checkbox';
    spamOutdoorCb.id = spamPlacementIds.Outdoor;
    spamOutdoorCb.hidden = true;
    var spamStyleContainerId = 'nt-lead-spam-styles-' + uid;
    var spamStyleContainer = document.createElement('div');
    spamStyleContainer.id = spamStyleContainerId;
    spamStyleContainer.hidden = true;
    root.appendChild(spamIndoorCb);
    root.appendChild(spamOutdoorCb);
    root.appendChild(spamStyleContainer);

    var nameFieldEl = form.querySelector('[data-nt-lead-field="name"]');
    var emailFieldEl = form.querySelector('[data-nt-lead-field="email"]');
    if (nameFieldEl && !nameFieldEl.id) nameFieldEl.id = 'nt-lead-name-' + uid;
    if (emailFieldEl && !emailFieldEl.id) emailFieldEl.id = 'nt-lead-email-' + uid;

    function fieldValue(key) {
      var input = form.querySelector('[data-nt-lead-field="' + key + '"]');
      return input ? input.value.trim() : '';
    }

    // selectedStyles maps id -> label (not a boolean) so the payload can send
    // the human-readable label ("Face lit / 3D Frontlit Signs") instead of
    // the internal id ("face_lit_3d_frontlit") without a second lookup pass.
    function selectedStyleLabels() {
      return Object.keys(selectedStyles)
        .filter(function (id) { return selectedStyles[id]; })
        .map(function (id) { return selectedStyles[id]; });
    }

    function selectedUsageLabels() {
      return Object.keys(selectedUsage).filter(function (key) { return selectedUsage[key]; });
    }

    function buildBasePayload() {
      return {
        name: fieldValue('name'),
        phone: fieldValue('phone'),
        email: fieldValue('email'),
        estimated_size: fieldValue('size'),
        details: fieldValue('details'),
        sign_placement: selectedUsageLabels().join(', '),
        signtypes: selectedStyleLabels(),
        lead_page_url: window.location.href,
        record_source: recordSource,
      };
    }

    function syncProspect() {
      var payload = buildBasePayload();
      payload.images = fileManager.urls();
      prospect.sync(payload);
    }
    var prospectTimer = null;
    function scheduleProspectSync() {
      clearTimeout(prospectTimer);
      prospectTimer = setTimeout(syncProspect, 700);
    }

    function showFieldError(key, message) {
      var errEl = form.querySelector('[data-nt-lead-fielderr="' + key + '"]');
      var input = form.querySelector('[data-nt-lead-field="' + key + '"]');
      if (errEl) {
        errEl.textContent = message || '';
        errEl.hidden = !message;
      }
      if (input) input.classList.toggle('has-error', !!message);
    }

    function showFileError(message) {
      var errEl = form.querySelector('[data-nt-lead-fielderr="file"]');
      if (errEl) {
        errEl.textContent = message || '';
        errEl.hidden = !message;
      }
      if (fileLabel) fileLabel.classList.toggle('has-error', !!message);
    }

    // Live field validation ticks + inline error clearing
    ['name', 'phone', 'email'].forEach(function (key) {
      var input = form.querySelector('[data-nt-lead-field="' + key + '"]');
      if (!input) return;
      input.addEventListener('input', function () {
        if (key === 'phone' && API.validators.formatPhone) {
          input.value = API.validators.formatPhone(input.value);
        }
        var ok = !API.validators.fieldError(key, input.value);
        input.classList.toggle('is-ok', ok);
        if (input.classList.contains('has-error')) showFieldError(key, API.validators.fieldError(key, input.value));
        scheduleProspectSync();
      });
      input.addEventListener('blur', function () {
        // Defer the error: on iOS the tap that blurs this field (e.g. on the logo
        // box just below) is dispatched as mousedown -> click. Showing the error
        // immediately pushes the layout down between the two, so the click lands
        // on empty space and the file picker never opens.
        setTimeout(function () {
          if (document.activeElement === input) return;
          showFieldError(key, API.validators.fieldError(key, input.value));
        }, 400);
        syncProspect();
      });
    });

    // Optional fields still feed the prospect audit trail
    ['size', 'details'].forEach(function (key) {
      var input = form.querySelector('[data-nt-lead-field="' + key + '"]');
      if (!input) return;
      input.addEventListener('input', scheduleProspectSync);
      input.addEventListener('blur', syncProspect);
    });

    // ── Multi-file logo/artwork upload — required, eager upload on select ──
    var fileInput = form.querySelector('[data-nt-lead-file]');
    var fileLabel = form.querySelector('[data-nt-lead-file-label]');
    var fileText = form.querySelector('[data-nt-lead-file-text]');
    var fileHint = form.querySelector('[data-nt-lead-file-hint]');
    var fileList = form.querySelector('[data-nt-lead-file-list]');
    var fileClearBtn = form.querySelector('[data-nt-lead-file-clear]');
    var DEFAULT_FILE_TEXT = fileText ? fileText.textContent : '';
    var DEFAULT_FILE_HINT = fileHint ? fileHint.textContent : '';

    function createFileManager() {
      return API.createFileManager({
        name: function () { return fieldValue('name') || 'lead'; },
        uuid: prospect.uuid,
        onChange: renderFileList,
      });
    }

    function renderFileList(files) {
      if (!files.length) {
        fileLabel.classList.remove('has-file');
        fileText.textContent = DEFAULT_FILE_TEXT;
        if (fileHint) { fileHint.classList.remove('err'); fileHint.textContent = DEFAULT_FILE_HINT; }
        if (fileClearBtn) fileClearBtn.hidden = true;
        fileList.innerHTML = '';
        showFileError('');
        return;
      }

      fileLabel.classList.add('has-file');
      fileText.textContent = files.length === 1 ? files[0].file.name : files.length + ' files selected';
      if (fileClearBtn) fileClearBtn.hidden = false;

      var hint = API.fileHint(files);
      if (fileHint) {
        fileHint.classList.toggle('err', hint.isError);
        fileHint.textContent = hint.text || DEFAULT_FILE_HINT;
      }

      fileList.innerHTML = files
        .map(function (entry) {
          var st = API.entryStatus(entry);
          var statusClass = st.cls;
          var statusText = escName(st.text);
          var retryBtn = st.canRetry
            ? '<button type="button" class="nt-qf-file-item-retry" data-retry-id="' + entry.id + '">Retry</button>'
            : '';
          return (
            '<div class="nt-qf-file-item">' +
            '<span class="nt-qf-file-item-name">' + escName(entry.file.name) + '</span>' +
            '<span class="nt-qf-file-item-status ' + statusClass + '">' + statusText + '</span>' +
            retryBtn +
            '<button type="button" class="nt-qf-file-item-remove" data-remove-id="' + entry.id + '" aria-label="Remove file">' +
            '<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18M6 6l12 12"/></svg>' +
            '</button>' +
            '</div>'
          );
        })
        .join('');

      // Every file added clears "no logo" error; done ones re-validate through submit.
      if (files.some(function (e) { return e.status === 'invalid' || e.status === 'error'; }) && !fileManager.hasLogo()) {
        // leave field error alone until submit re-checks it
      } else {
        showFileError('');
      }
      scheduleProspectSync();
    }

    if (fileInput) {
      // Clear before the picker opens so choosing the same file again still
      // fires "change". Uploads use their own in-memory copy of each file
      // (NTQuoteAPI.createFileManager), so nothing depends on this input's
      // file list after selection — never rewrite it mid-upload.
      fileInput.addEventListener('click', function () {
        fileInput.value = '';
      });
      fileInput.addEventListener('change', function () {
        if (fileInput.files && fileInput.files.length) fileManager.addFiles(fileInput.files);
      });
      ['dragover', 'dragenter'].forEach(function (ev) {
        fileLabel.addEventListener(ev, function (e) { e.preventDefault(); fileLabel.classList.add('drag'); });
      });
      ['dragleave', 'drop'].forEach(function (ev) {
        fileLabel.addEventListener(ev, function () { fileLabel.classList.remove('drag'); });
      });
      fileLabel.addEventListener('drop', function (e) {
        e.preventDefault();
        if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files.length) fileManager.addFiles(e.dataTransfer.files);
      });
    }

    if (fileList) {
      fileList.addEventListener('click', function (e) {
        var retryBtn = e.target.closest('[data-retry-id]');
        if (retryBtn) { fileManager.retry(retryBtn.getAttribute('data-retry-id')); return; }
        var removeBtn = e.target.closest('[data-remove-id]');
        if (removeBtn) fileManager.remove(removeBtn.getAttribute('data-remove-id'));
      });
    }

    if (fileClearBtn) {
      fileClearBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        fileManager.clear();
      });
    }

    // Indoor / outdoor toggle — multi-select, both can be active at once
    root.querySelectorAll('[data-nt-lead-usage]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var key = btn.getAttribute('data-nt-lead-usage');
        selectedUsage[key] = !selectedUsage[key];
        btn.classList.toggle('is-selected', selectedUsage[key]);
        var spamCb = key === 'Indoor' ? spamIndoorCb : key === 'Outdoor' ? spamOutdoorCb : null;
        if (spamCb) spamCb.checked = !!selectedUsage[key];
        syncProspect();
      });
    });

    // Style preference grid, built from the shared data file
    var styleGrid = root.querySelector('[data-nt-lead-styles]');
    var styleDefs = (window.ntSignData && window.ntSignData.STYLE_DEFS) || [];
    if (styleGrid && styleDefs.length) {
      styleDefs.forEach(function (s) {
        var cell = document.createElement('div');
        cell.className = 'nt-qf-style';
        var parts = s.label.split(' / ');
        var main = parts[0];
        var sub = parts.slice(1).join(' / ');
        cell.innerHTML =
          '<span class="nt-qf-style-check"><svg viewBox="0 0 24 24"><path d="M20 6L9 17l-5-5"></path></svg></span>' +
          '<img src="' + s.img + '" alt="' + s.label + '" loading="lazy">' +
          '<div class="nt-qf-style-n"><span class="nt-qf-style-n-main">' + main + '</span>' +
          (sub ? '<span class="nt-qf-style-n-sub">' + sub + '</span>' : '') +
          '</div>' +
          (s.tag ? '<div class="nt-qf-style-tag">' + s.tag + '</div>' : '');
        var spamStyleCb = document.createElement('input');
        spamStyleCb.type = 'checkbox';
        spamStyleContainer.appendChild(spamStyleCb);
        cell.addEventListener('click', function () {
          selectedStyles[s.id] = selectedStyles[s.id] ? '' : s.label;
          cell.classList.toggle('is-selected', !!selectedStyles[s.id]);
          spamStyleCb.checked = !!selectedStyles[s.id];
          syncProspect();
        });
        styleGrid.appendChild(cell);
      });
    }

    function showError(message) {
      errorBox.textContent = message;
      errorBox.hidden = false;
    }

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      errorBox.hidden = true;

      var requiredFields = ['name', 'phone', 'email'];
      var firstInvalid = null;

      requiredFields.forEach(function (key) {
        var input = form.querySelector('[data-nt-lead-field="' + key + '"]');
        var msg = API.validators.fieldError(key, input.value);
        showFieldError(key, msg);
        input.classList.toggle('is-ok', !msg);
        if (msg && !firstInvalid) firstInvalid = input;
      });

      var fileMsg = fileManager.hasLogo() || fileManager.isUploading() ? '' : 'Please upload your logo so we can build your mockup.';
      showFileError(fileMsg);
      if (fileMsg && !firstInvalid) firstInvalid = fileLabel || fileInput;

      if (firstInvalid) {
        firstInvalid.focus();
        firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      var name = form.querySelector('[data-nt-lead-field="name"]');
      var email = form.querySelector('[data-nt-lead-field="email"]');

      var submitBtn = form.querySelector('[type="submit"]');
      var submitText = submitBtn ? submitBtn.querySelector('span') : null;
      var originalText = submitText ? submitText.textContent : 'Get My Free Mockup';
      if (submitBtn) submitBtn.disabled = true;
      if (submitText) submitText.textContent = 'Submitting';

      // Spam gate helpers live in assets/spam-gate.js (window.SpamGate). It
      // owns window.leadFormState, the scroll listener, and the 4-condition
      // check. cfg points at the hidden mirror checkboxes set up above,
      // since this form's real pickers aren't checkboxes.
      var evaluation = window.SpamGate && window.SpamGate.evaluateSpam({
        signTypeSelector: '#' + spamStyleContainerId + ' input[type=checkbox]',
        placementIds: [spamPlacementIds.Indoor, spamPlacementIds.Outdoor],
      });

      if (evaluation && evaluation.isSpam) {
        console.log('No conversion tags fired: spam gate flagged this submission.', evaluation);
        window.SpamGate.submitFormB(form, {
          spamReason: 'ntlh',
          emailFieldId: emailFieldEl ? emailFieldEl.id : 'email',
          nameFieldId: nameFieldEl ? nameFieldEl.id : 'name',
          onDone: function () {
            showThankYou();
            if (submitBtn) submitBtn.disabled = false;
            if (submitText) submitText.textContent = originalText;
          },
        });
        return;
      }
      if (evaluation) window.SpamGate.attachDiagnosticMeta(form, evaluation, 'ntlh');
      // Mark end of this fill cycle so the next submit's elapsedMs is measured
      // from now, not from page load. Only formOpenedAt is reset; maxScroll persists.
      if (window.SpamGate) window.SpamGate.resetLeadFormState();

      function showThankYou() {
        if (firstNameEl) firstNameEl.textContent = (name.value.trim().split(' ')[0]) || 'friend';
        if (emailEl) emailEl.textContent = email.value.trim();
        if (phoneEl) phoneEl.textContent = fieldValue('phone');
        form.hidden = true;
        successView.hidden = false;
        successView.scrollIntoView({ behavior: 'smooth', block: 'start' });
        try {
          history.pushState(null, '', window.location.pathname + '?thanks-for-response');
        } catch (e) { }
      }

      // A failed submit must NOT show the same screen as a real success —
      // that hid the fact that no lead was ever sent (e.g. logo upload
      // never completed), so nobody noticed the lead was missing. Keep the
      // form visible and surface a real error instead.
      function showSubmitError(err, errorContext) {
        try {
          if (typeof window.logClientError === 'function') {
            window.logClientError(
              errorContext || 'nt_lead_hero_submit',
              err || new Error('form submit failed'),
              prospect ? prospect.uuid : null,
            );
          }
        } catch (e) { /* never let the logger break the caller */ }
        var msg = err && err.message === 'Logo upload did not complete'
          ? 'Your logo failed to upload, so we couldn’t send your request. Please retry the upload above and submit again.'
          : 'Something went wrong submitting your request. Please try again.';
        showError(msg);
        errorBox.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Marks the failed attempt in the URL (distinct from a real
        // ?thanks-for-response) without navigating away from the form or
        // showing the success view — kept for reporting/analytics that
        // differentiate the two outcomes.
        try {
          history.pushState(null, '', window.location.pathname + '?thanks-for-submission');
        } catch (e) { }
      }

      // Fire the lead submit as soon as the logo upload is done — don't gate
      // it behind the prospect audit-trail chain (getChain()), which can
      // still have pending debounced autosave requests in flight and would
      // otherwise make /api/leads visibly wait behind those in the network tab.
      fileManager.waitForUploads()
        .then(function () {
          if (!fileManager.hasLogo()) throw new Error('Logo upload did not complete');
          var payload = buildBasePayload();
          Object.assign(payload, fileManager.leadImageFields());
          payload.tracking_metadata = trackingMetadataWithOppref();
          return leadTracker.submit(payload);
        })
        .then(function (data) {
          if (prospect) prospect.finalize();
          // Show the thank-you screen first (sets ?thanks-for-response), then fire tags.
          showThankYou();
          var emailVal = email.value.trim();
          // /api/leads (nt-quote-api.js) doesn't compute geo-IP server-side like the
          // legacy /api/no-approval endpoint did — it just echoes back whatever
          // `location` string the client sent. That string is set client-side in
          // layout/theme.liquid as "city, region, country, timezone, ip" (ip last),
          // and is already in localStorage before submit — read it from there.
          try {
            var location = (window.safeLocalStorage || localStorage).getItem('location') || (window.safeLocalStorage || localStorage).getItem('locationxh') || '';
            var locationParts = location ? location.split(', ') : [];
            var ipAddress = locationParts.length ? locationParts[locationParts.length - 1] : '';
            var spamIp = !!(ipAddress && (ipAddress.indexOf('2603:7000:b040:1ea1:') !== -1 || ipAddress.indexOf('174.255.203') !== -1));
            var spamEmail = !!(emailVal && emailVal.toLowerCase().indexOf('fastsigns') !== -1);
            if (!spamIp && !spamEmail) {
              // Google Ads first, isolated so a throw here can't skip the OpenAI event
              try {
                if (typeof gtag_report_conversion === 'function') gtag_report_conversion();
              } catch (gtagError) {
                console.error('gtag_report_conversion error:', gtagError);
              }
              // Open AI pixel lead conversion (isolated so it can't affect the Google Ads call)
              try {
                if (typeof oaiq === 'function') {
                  var oaiqOptions = data && data.airtableRecordId ? { event_id: 'lead_' + data.airtableRecordId } : undefined;
                  if (oaiqOptions) {
                    oaiq('measure', 'lead_created', { type: 'customer_action' }, oaiqOptions);
                  } else {
                    oaiq('measure', 'lead_created', { type: 'customer_action' });
                  }
                  console.log('oaiq lead_created fired', oaiqOptions || '');
                } else {
                  console.log('oaiq lead_created NOT fired: oaiq is not loaded');
                }
              } catch (oaiError) {
                console.error('oaiq lead_created error:', oaiError);
              }
            } else {
              console.log('gtag_report_conversion skipped for flagged lead:', { ipAddress: ipAddress, email: emailVal, spamIp: spamIp, spamEmail: spamEmail });
            }
          } catch (error) {
            console.error('Error during gtag_report_conversion:', error);
          }
          try {
            if (typeof bingTagFunction === 'function') bingTagFunction(window, document, 'script', '//bat.bing.com/bat.js', 'uetq');
          } catch (e) {
            console.error('bingTagFunction error:', e);
          }
          try {
            if (typeof kalviyo === 'function') kalviyo(emailVal);
          } catch (e) {
            console.error('kalviyo error:', e);
          }
          // /api/leads returns { data: { is_spam }, airtableRecordId } — the lead's
          // own Airtable record id, not the old { airtable_data: { body: { records }}} shape.
          try {
            var leadRecord = (data && data.data) || {};
            if (!leadRecord.is_spam && data && data.airtableRecordId && typeof checkisDuplicate === 'function') {
              checkisDuplicate(data.airtableRecordId, emailVal, name.value.trim(), 'SignMakerz', fieldValue('phone'));
            }
          } catch (e) {
            console.error('checkisDuplicate error:', e);
          }
        })
        .catch(function (err) {
          showSubmitError(err, 'nt_lead_hero_submit');
        })
        .finally(function () {
          if (submitBtn) submitBtn.disabled = false;
          if (submitText) submitText.textContent = originalText;
        });
    });

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) syncProspect();
    });
    window.addEventListener('pagehide', syncProspect);

    if (resetBtn) {
      resetBtn.addEventListener('click', function () {
        form.reset();
        form.hidden = false;
        successView.hidden = true;
        var submitBtn = form.querySelector('[type="submit"]');
        var submitText = submitBtn ? submitBtn.querySelector('span') : null;
        if (submitText) submitText.textContent = 'Get My Free Mockup';
        root.querySelectorAll('.is-ok').forEach(function (el) { el.classList.remove('is-ok'); });
        root.querySelectorAll('.has-error').forEach(function (el) { el.classList.remove('has-error'); });
        root.querySelectorAll('[data-nt-lead-fielderr]').forEach(function (el) { el.hidden = true; el.textContent = ''; });
        root.querySelectorAll('.is-selected').forEach(function (el) { el.classList.remove('is-selected'); });
        selectedStyles = {};
        selectedUsage = {};
        spamIndoorCb.checked = false;
        spamOutdoorCb.checked = false;
        spamStyleContainer.querySelectorAll('input[type=checkbox]').forEach(function (cb) { cb.checked = false; });
        fileManager.clear();
        prospect = API.createProspectTracker();
        leadTracker = API.createLeadTracker(prospect.uuid);
        fileManager = createFileManager();
      });
    }
  }

  document.querySelectorAll('[data-nt-lead-hero]').forEach(init);
  document.addEventListener('shopify:section:load', function (e) {
    e.target.querySelectorAll('[data-nt-lead-hero]').forEach(init);
  });
})();
